import { useCallback, useContext, useEffect, useState } from "react";
import { Button, Container, Form, Spinner, Alert, Row, Col, Pagination, InputGroup } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faTimes } from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../../../../context/AuthContext";
import { UsuariosxPlataformaInterface } from "../../../../interfaces/UsuariosxPlataforma";
import { API } from "../../../../global/global";
import axios from "axios";
import { toast } from "react-toastify";

interface PaginatedResponse {
  data: UsuariosxPlataformaInterface[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

const PlataformasAsignar = () => {
  const { idPlataforma } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [usuarios, setUsuarios] = useState<UsuariosxPlataformaInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [plataformaNombre, setPlataformaNombre] = useState("");

  const fetchUsuarios = useCallback(async (page = 1, searchTerm = "") => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/usuarios_plataforma`,
        { 
          plataforma_id: idPlataforma,
          page,
          per_page: 10,
          search: searchTerm
        },
        {
          headers: {
            Authorization: `Bearer ${authContext.token}`,
          },
        }
      );
      
      const data: PaginatedResponse = response.data;
      setUsuarios(data.data);
      setCurrentPage(data.current_page);
      setLastPage(data.last_page);
      setTotal(data.total);
      if ((data as any).plataforma_nombre) {
        setPlataformaNombre((data as any).plataforma_nombre);
      }
    } catch (error) {
      console.error("Error fetching usuarios:", error);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  }, [authContext.token, idPlataforma]);

  useEffect(() => {
    fetchUsuarios(1, search);
  }, [fetchUsuarios]);

  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchUsuarios(1, searchTerm);
    }, 500);
    
    setSearchTimeout(timeout);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchUsuarios(page, search);
  };

  const guardarPlataformas = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    
    try {
      await axios.post(
        `${API}/saveusuarios_plataforma`,
        { plataforma_id: idPlataforma, usuarios: usuarios },
        {
          headers: {
            Authorization: `Bearer ${authContext.token}`,
          },
        }
      );
      
      toast.success("Usuarios asignados correctamente");
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Error saving usuarios:", error);
      toast.error("Error al guardar asignaciones");
    } finally {
      setSaving(false);
    }
  };

  const handleUsuarioChange = (index: number, checked: boolean) => {
    const updatedUsuarios = [...usuarios];
    // Actualizar el campo asignado basado en el toggle switch
    updatedUsuarios[index] = {
      ...updatedUsuarios[index],
      asignado: checked
    };
    setUsuarios(updatedUsuarios);
  };

  const handleCancel = () => {
    navigate("/main/catalogos/plataformas");
  };

  const renderPagination = () => {
    if (lastPage <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(lastPage, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="justify-content-center mt-4">
        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="me-2" />
        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="me-2" />
        {items}
        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === lastPage} className="ms-2" />
        <Pagination.Last onClick={() => handlePageChange(lastPage)} disabled={currentPage === lastPage} className="ms-2" />
      </Pagination>
    );
  };

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <h1>Asignar Usuarios a Plataforma {plataformaNombre || idPlataforma}</h1>
        </Col>
        <Col xs="auto">
          <Button variant="secondary" onClick={handleCancel}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Cancelar
          </Button>
        </Col>
      </Row>

      {/* Buscador */}
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar por nombre, apellidos o correo..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={6} className="text-end">
          <small className="text-muted">
            Mostrando {usuarios.length} de {total} usuarios
          </small>
        </Col>
      </Row>

      {loading ? (
        <Container className="d-flex justify-content-center">
          <Spinner animation="grow" variant="info" />
        </Container>
      ) : (
        <>
          <Form onSubmit={guardarPlataformas}>
            {usuarios.length === 0 ? (
              <Alert variant="info">
                No se encontraron usuarios {search && `que coincidan con "${search}"`}
              </Alert>
            ) : (
              usuarios.map((usuario, index) => (
                <div key={usuario.id} className="mb-3 p-3 border rounded d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{usuario.nombre} {usuario.apellido_paterno} {usuario.apellido_materno ?? ""}</strong>
                    <br />
                    <small className="text-muted">{usuario.correo}</small>
                  </div>
                  <Form.Check
                    type="switch"
                    id={`usuario-${usuario.id}`}
                    checked={(usuario as any).asignado || (usuario.pivot ? true : false)}
                    onChange={(event) => handleUsuarioChange(index, event.target.checked)}
                    label=""
                  />
                </div>
              ))
            )}
            
            {usuarios.length > 0 && (
              <Row className="mt-4">
                <Col>
                  <Button variant="secondary" onClick={handleCancel} className="me-2">
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Guardando...
                      </>
                    ) : (
                      "Guardar Cambios"
                    )}
                  </Button>
                </Col>
              </Row>
            )}
          </Form>

          {/* Paginación */}
          {renderPagination()}
        </>
      )}
    </Container>
  );
};

export default PlataformasAsignar;
